import { createSelector } from 'reselect';

import { getMainClientInfo } from '../Main/selectors';
import { initialState } from './reducer';
import { FETCH_ERROR_MESSAGE } from './constants';

/**
  * Direct selector to the Help state domain
  */
const selectHelpDomain = (state) => state.help || initialState;

/**
 * Direct selector to Help props
 * This selector can be used to grab React Router props that maybe passed down
 */
const selectHelpProps = (state, props) => props;

/**
  * Other specific selectors
  */

/**
  * @description selects target error via prop
  */
const selectHelpErrors = (prop) => createSelector(
  selectHelp('errors'),
  (errorState) => errorState[prop],
);

/**
 * @description Selects and formats errors for screen reader
 */
const selectHelpNotifications = () => createSelector(
  selectHelpErrors('bannerError'),
  selectHelpErrors('serviceError'),
  (bannerError, serviceError) => {
    const notifications = [];
    if (serviceError) {
      notifications.push(serviceError);
    }
    if (bannerError) {
      notifications.push(bannerError);
    }
    return notifications;
  },
);

/**
  * @description Selects collection department phone number
  */
const selectCollectionsPhoneNumber = () => createSelector(
  getMainClientInfo('contactInfoPageCollectionsDepartmentPhoneNumber'),
  (collectionsDepartmentPhoneNumber) => collectionsDepartmentPhoneNumber,
);

/**
  * @description Selects hardship assistance phone number
  */
const selectHardshipAssistancePhoneNumber = () => createSelector(
  getMainClientInfo('hardshipContactCardPhoneNumber'),
  (hardshipContactCardPhoneNumber) => hardshipContactCardPhoneNumber,
);

/**
  * @description Selects faq categories
  */
const selectFaqCategories = () => createSelector(
  selectHelp('faqCategories'),
  (faqCategories) => faqCategories.map(({ id, name }) => ({ id, label: name })),
);

/**
  * @description Selects faq currently selected category
  */
const selectFaqCurrentCategory = () => createSelector(
  selectHelp('faqCategories'),
  selectHelp('faqSelectedCategory'),
  (categories, id) => id ? categories.find((category) => category.id === id).name : '',
);

/**
  * @description Selects faq currently selected question
  */
const selectFaqCurrentQuestion = () => createSelector(
  selectHelp('faqCategories'),
  selectHelp('faqSelectedCategory'),
  selectHelp('faqSelectedQuestion'),
  (categories, id, idx) => {
    if (id && idx !== null) {
      const questions = categories.find((category) => category.id === id).faqs;
      return questions[idx];
    }
    return { answer: '', question: '' };
  },
);

/**
  * @description Selects faq category questions
  */
const selectFaqQuestions = () => createSelector(
  selectHelp('faqCategories'),
  selectHelp('faqSelectedCategory'),
  (categories, id) => {
    if (id) {
      const questions = categories.find((category) => category.id === id).faqs;
      return questions.map(({ question }, idx) => ({ id: idx, label: question }));
    }
    return [];
  },
);

/**
 * @description Selects view
 */
const selectPageView = () => createSelector(
  selectHelpProps,
  (props) => props.match.params.view || 'main',
);

/**
 * @description selects error message with correct phone number
 */
const selectServiceErrorMessage = () => createSelector(
  getMainClientInfo('customerServicePhoneNumber'),
  (customerServicePhoneNumber) => `${FETCH_ERROR_MESSAGE} ${customerServicePhoneNumber}.`,
);

/**
  * Default selector used by Help
  */
const selectHelp = (prop) => createSelector(
  selectHelpDomain,
  (state) => state[prop],
);

export default selectHelp;
export {
  selectCollectionsPhoneNumber,
  selectHardshipAssistancePhoneNumber,
  selectHelpErrors,
  selectHelpNotifications,
  selectHelpProps,
  selectFaqCategories,
  selectFaqCurrentCategory,
  selectFaqCurrentQuestion,
  selectFaqQuestions,
  selectPageView,
  selectServiceErrorMessage,
};
